import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Plus, Search, Building2, User, Monitor, Trash2, CheckSquare, Square } from 'lucide-react';

const statusLabel = {
  open: 'Offen',
  in_progress: 'In Bearbeitung',
  waiting: 'Wartend',
  followup: 'Wiedervorlage',
  closed: 'Geschlossen',
};

const statusColor = {
  open: 'bg-blue-100 text-blue-700 hover:bg-blue-200',
  in_progress: 'bg-amber-100 text-amber-700 hover:bg-amber-200',
  waiting: 'bg-orange-100 text-orange-700 hover:bg-orange-200',
  followup: 'bg-purple-100 text-purple-700 hover:bg-purple-200',
  closed: 'bg-gray-100 text-gray-500 hover:bg-gray-200',
};

const allStatuses = ['open', 'in_progress', 'waiting', 'followup', 'closed'];

export default function Tickets() {
  const [tickets, setTickets] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState('');
  const [searchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') ? new Set([searchParams.get('status')]) : new Set(['open', 'in_progress', 'waiting', 'followup']));
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const [selected, setSelected] = useState(new Set());
  const [deleting, setDeleting] = useState(false);
  const customerIdFilter = searchParams.get('customer_id');
  const supplierIdFilter = searchParams.get('supplier_id');
  const supplierNameFilter = searchParams.get('supplier_name');
  const customerNameFilter = searchParams.get('customer_name');
  const contactIdFilter = searchParams.get('contact_id');
  const contactNameFilter = searchParams.get('contact_name');
  const deviceIdFilter = searchParams.get('device_id');
  const deviceNameFilter = searchParams.get('device_name');
  const entityFilterActive = customerIdFilter || supplierIdFilter || contactIdFilter || deviceIdFilter;

  const load = () => {
    setLoading(true);
    const custId = searchParams.get('customer_id');
    const suppId = searchParams.get('supplier_id');
    const contId = searchParams.get('contact_id');
    const devId = searchParams.get('device_id');
    const params = custId ? `?customer_id=${custId}`
      : suppId ? `?supplier_id=${suppId}`
      : contId ? `?contact_id=${contId}`
      : devId ? `?device_id=${devId}` : '';
    api.get(`/tickets${params}`).then(res => {
      setTickets(res.data);
      setFiltered(res.data);
    }).catch(() => toast.error('Fehler beim Laden der Tickets'))
    .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [customerIdFilter, supplierIdFilter, contactIdFilter, deviceIdFilter]);

  useEffect(() => {
    let result = tickets;
    if (search) {
      result = result.filter(t =>
        t.title.toLowerCase().includes(search.toLowerCase()) ||
        t.ticket_number.toLowerCase().includes(search.toLowerCase()) ||
        t.customer_name?.toLowerCase().includes(search.toLowerCase()) ||
        t.supplier_name?.toLowerCase().includes(search.toLowerCase())
      );
    }
    if (statusFilter.size > 0) {
      result = result.filter(t => statusFilter.has(t.status));
    }
    setFiltered(result);
  }, [search, statusFilter, tickets, entityFilterActive]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [customerIdFilter]);

  const handleStatusChange = async (e, ticketId, newStatus) => {
    e.stopPropagation();
    setOpenDropdown(null);
    try {
      await api.patch('/tickets/' + ticketId + '/status', { status: newStatus });
      toast.success('Status geaendert');
      load();
    } catch (err) {
      toast.error('Fehler beim Aendern');
    }
  };

  const handleDeleteSelected = async () => {
    if (!window.confirm(`${selected.size} Ticket(s) wirklich löschen?`)) return;
    setDeleting(true);
    try {
      await Promise.all([...selected].map(id => api.delete('/tickets/' + id)));
      toast.success(selected.size + ' Ticket(s) gelöscht');
      setSelected(new Set());
      load();
    } catch (err) {
      const msg = err.response?.data?.error || '';
      if (msg.includes('service_reports') || msg.includes('Servicebericht')) {
        toast.error('Ticket hat einen verknüpften Servicebericht und kann nicht gelöscht werden.');
      } else if (msg.includes('journal')) {
        toast.error('Ticket hat verknüpfte Journaleinträge und kann nicht gelöscht werden.');
      } else {
        toast.error('Fehler beim Löschen: ' + (msg || 'Unbekannter Fehler'));
      }
    } finally {
      setDeleting(false);
    }
  };

  const toggleSelect = (e, id) => {
    e.stopPropagation();
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map(t => t.id)));
    }
  };

  if (loading) return <div className="text-gray-400 text-sm">Laden...</div>;

  return (
    <div>
      {(customerNameFilter || contactNameFilter || deviceNameFilter) && (
        <div className="flex items-center gap-2 mb-4 px-4 py-2.5 bg-primary-50 border border-primary-100 rounded-xl">
          {deviceNameFilter ? <Monitor size={15} className="text-primary-500 flex-shrink-0" />
            : contactNameFilter ? <User size={15} className="text-primary-500 flex-shrink-0" />
            : <Building2 size={15} className="text-primary-500 flex-shrink-0" />}
          <span className="text-sm text-primary-700 font-medium">
            {deviceNameFilter ? 'Tickets für Gerät: ' : contactNameFilter ? 'Tickets von: ' : 'Tickets von: '}
            <strong>{decodeURIComponent(deviceNameFilter || contactNameFilter || customerNameFilter)}</strong>
          </span>
          <button
            onClick={() => navigate('/tickets')}
            className="ml-auto text-xs text-primary-400 hover:text-primary-600 transition-colors"
          >
            Filter entfernen ×
          </button>
        </div>
      )}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 mb-4 px-4 py-2.5 bg-red-50 border border-red-100 rounded-xl">
          <span className="text-sm text-red-700 font-medium">{selected.size} ausgewählt</span>
          <button
            onClick={handleDeleteSelected}
            disabled={deleting}
            className="flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50 ml-auto"
          >
            <Trash2 size={13} />
            {deleting ? 'Löscht...' : 'Löschen'}
          </button>
          <button onClick={() => setSelected(new Set())} className="text-xs text-red-400 hover:text-red-600">
            Abbrechen
          </button>
        </div>
      )}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Tickets</h2>
          <p className="text-gray-500 text-sm mt-1">{filtered.length} Tickets</p>
        </div>
        <button
          onClick={() => navigate('/tickets/new')}
          className="flex items-center gap-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors shadow-lg shadow-primary-500/25"
        >
          <Plus size={16} />
          Neues Ticket
        </button>
      </div>
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Suche nach Titel, Nummer oder Kunde..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white shadow-sm"
          />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto sm:flex-wrap pb-1 sm:pb-0 -mx-1 px-1 sm:mx-0 sm:px-0">
          {allStatuses.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(prev => {
                const next = new Set(prev);
                next.has(s) ? next.delete(s) : next.add(s);
                return next;
              })}
              className={"text-xs font-medium px-3 py-2 rounded-xl border transition-colors shrink-0 " + (statusFilter.has(s) ? "bg-primary-500 border-primary-500 text-white shadow-sm" : "bg-white border-gray-200 text-gray-500 hover:border-gray-300")}
            >
              {statusLabel[s]}
            </button>
          ))}
        </div>
      </div>
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm">
        <div className="divide-y divide-gray-50">
          {filtered.length === 0 && (
            <p className="text-sm text-gray-400 p-6">Keine Tickets gefunden</p>
          )}
          {filtered.map(ticket => (
            <div
              key={ticket.id}
              onClick={() => navigate('/tickets/' + ticket.id)}
              className={'px-3 sm:px-5 py-3 sm:py-4 flex items-center justify-between cursor-pointer transition-colors border-l-2 ' + (ticket.supplier_id ? 'bg-amber-50 hover:bg-amber-100 border-amber-300' : 'hover:bg-gray-50 border-transparent') + (selected.has(ticket.id) ? ' bg-primary-50/50' : '')}
            >
              <button onClick={e => toggleSelect(e, ticket.id)} className="mr-2 sm:mr-3 p-1 -m-1 text-gray-300 hover:text-primary-500 transition-colors shrink-0">
                {selected.has(ticket.id) ? <CheckSquare size={16} className="text-primary-500" /> : <Square size={16} />}
              </button>
              <div className="min-w-0 flex-1">
                <p className="text-base font-bold text-gray-900">
                  {ticket.ticket_number} · {ticket.title}
                </p>
                <p className="text-sm text-gray-500 mt-0.5 truncate">
                  {ticket.supplier_name || ticket.customer_name || ''}
                  {!ticket.supplier_name && ticket.contact_name && ticket.contact_id ? (
                    <> · <button
                      onClick={e => { e.stopPropagation(); navigate('/tickets?contact_id=' + ticket.contact_id + '&contact_name=' + encodeURIComponent(ticket.contact_name)); }}
                      className="hover:text-primary-600 hover:underline transition-colors"
                    >{ticket.contact_name}</button></>
                  ) : (ticket.supplier_name && ticket.supplier_contact_name) ? ' · ' + ticket.supplier_contact_name : ''}
                </p>
              </div>
              <div className="flex items-center gap-2 sm:gap-3 ml-2 sm:ml-4 shrink-0" ref={openDropdown === ticket.id ? dropdownRef : null}>
                <span className="text-sm font-semibold text-gray-400 hidden md:block">
                  {new Date(ticket.created_at).toLocaleString('de-DE', {day:'2-digit',month:'2-digit',year:'numeric',hour:'2-digit',minute:'2-digit'})}
                </span>
                <div className="relative">
                  <button
                    onClick={e => { e.stopPropagation(); setOpenDropdown(openDropdown === ticket.id ? null : ticket.id); }}
                    className={'text-xs font-medium px-2.5 py-1.5 sm:py-1 rounded-full transition-colors ' + (statusColor[ticket.status] || '')}
                  >
                    {statusLabel[ticket.status]}
                  </button>
                  {openDropdown === ticket.id && (
                    <div className="absolute right-0 top-8 bg-white border border-gray-200 rounded-xl shadow-lg z-10 py-1 min-w-40">
                      {allStatuses.filter(s => s !== ticket.status).map(s => (
                        <button
                          key={s}
                          onClick={e => handleStatusChange(e, ticket.id, s)}
                          className="w-full text-left px-4 py-2 text-xs text-gray-700 hover:bg-gray-50 transition-colors"
                        >
                          {statusLabel[s]}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
